<template>
    <div class="RiskProfileAssignmentRuleBased">
        <LogoLoader v-if="loadingCustomProperties" />
        <div
            v-else-if="!loadingCustomProperties"
            class="RiskProfileAssignmentRuleBased__content"
        >
            <div class="RiskProfileAssignmentRuleBased__header">
                <p class="RiskProfileAssignmentRuleBased__headerTitle">
                    Define matching rules
                </p>
                <p class="RiskProfileAssignmentRuleBased__headerDescription">
                    Create conditional rules to assign this profile to entities that match specific criteria.
                </p>
            </div>
            <div
                v-for="(basicRules, rulesGroupIndex) in localRules"
                :key="rulesGroupIndex"
                class="RiskProfileAssignmentRuleBased__orRule"
            >
                <div class="flex text-eidDarkBlue-50">
                    <span
                        v-if="rulesGroupIndex > 0"
                        class="mx-2 text-weight-medium"
                    >
                        OR
                    </span>
                </div>

                <div class="RiskProfileAssignmentRuleBased__orRuleBody">
                    <div
                        v-for="(basicRule, basicRuleIndex) in basicRules"
                        :key="basicRuleIndex"
                        class="flex items-center gap-4"
                    >
                        <span class="text-weight-medium">
                            IF
                        </span>
                        <div class="RiskProfileAssignmentRuleBased__andRuleContainer">
                            <div class="RiskProfileAssignmentRuleBased__andRuleContent">
                                <NewDropdown
                                    class="RiskProfileAssignmentRuleBased__conditionFieldDropdown"
                                    label="Select Property"
                                    hide-selected-chip
                                    :disabled="isDisabled"
                                    :options="customPropertiesOptions"
                                    :selected="convertCustomPropertyToDropdownOption(basicRule.insuredFieldKey)"
                                    @input="onCustomPropertyNameChange(rulesGroupIndex, basicRuleIndex, $event)"
                                />
                                <NewDropdown
                                    class="RiskProfileAssignmentRuleBased__conditionOperatorDropdown"
                                    :disabled="isDisabled"
                                    :options="getAvailableOperators(basicRule)"
                                    :selected="convertToOperatorOption(basicRule.operator)"
                                    @input="onOperatorChange(rulesGroupIndex, basicRuleIndex, $event)"
                                />
                                <NewDropdown
                                    v-if="getCustomPropertyType(basicRule) === 'boolean'"
                                    class="RiskProfileAssignmentRuleBased__conditionValueDropdown
                            RiskProfileAssignmentRuleBased__conditionValue"
                                    :options="booleanOptions"
                                    :selected="convertToBooleanOption(basicRule.value)"
                                    :disabled="isDisabled || !isCustomPropertySelected(basicRule)"
                                    @input="onBooleanValueChange(rulesGroupIndex, basicRuleIndex, $event)"
                                />
                                <NewDropdown
                                    v-else-if="getCustomPropertyType(basicRule) === 'CREDENTIAL_TYPES'"
                                    class="RiskProfileAssignmentRuleBased__riskRequirementTypeValueDropdown
                            RiskProfileAssignmentRuleBased__conditionValue"
                                    :disabled="isDisabled"
                                    :options="requirementTypeOptions"
                                    :selected="convertToRiskRequirementTypeOption(basicRule.value)"
                                    @input="onRiskRequirementTypeValueChange(rulesGroupIndex, basicRuleIndex, $event)"
                                />
                                <NewDropdown
                                    v-else-if="getCustomPropertyType(basicRule) === 'ENUM'"
                                    class="RiskProfileAssignmentRuleBased__conditionValueDropdown
                            RiskProfileAssignmentRuleBased__conditionValue"
                                    :options="getEnumValueOptions(basicRule)"
                                    :selected="convertToEnumValueOption(basicRule.value)"
                                    :disabled="isDisabled || !isCustomPropertySelected(basicRule)"
                                    @input="onEnumValueChange(rulesGroupIndex, basicRuleIndex, $event)"
                                />
                                <input
                                    v-else
                                    class="RiskProfileAssignmentRuleBased__conditionValue
                             RiskProfileAssignmentRuleBased__input"
                                    :type="getInputValueType(basicRule)"
                                    :value="basicRule.value"
                                    :disabled="isDisabled || !isCustomPropertySelected(basicRule)"
                                    @input="onValueChange(rulesGroupIndex, basicRuleIndex, $event)"
                                >
                            </div>
                            <NewButton
                                variant="flat"
                                color="secondary"
                                size="icon-sm"
                                :disabled="isDisabled"
                                aria-label="Remove rule"
                                @click="removeBasicRule(rulesGroupIndex, basicRuleIndex)"
                            >
                                <EidIcon :icon-src="faXmark" />
                            </NewButton>
                        </div>
                    </div>
                    <NewButton
                        variant="outline"
                        :disabled="isDisabled"
                        @click="addBasicRule(rulesGroupIndex)"
                    >
                        <EidIcon :icon-src="faPlus" />
                        And
                    </NewButton>
                </div>
            </div>
            <NewButton
                class="mt-5"
                variant="outline"
                :disabled="isDisabled || isAtMaxRuleGroups"
                @click="addRulesGroup"
            >
                <EidIcon :icon-src="faPlus" />
                Or if ...
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
.RiskProfileAssignmentRuleBased {
    display: flex;
    flex-direction: column;
    padding: 16px;
    background-color: var(--eidBgGrayTones-lightest);
    border: 1px solid var(--eidTechGray-25);
    border-radius: var(--primary-border-radius);

    p {
        margin: 0;
        font-size: 14px;
    }

    &__headerTitle {
        font-weight: 500;
    }

    &__headerDescription {
        color: var(--eidSecureBlue-75);
    }

    &--loading {
        justify-content: center;
    }

    .Dropdown {
        width: unset;
    }

    .Button {
        padding: 0;
        color: #29b496;
    }

    &__orRule {
        display: flex;
        flex-direction: column;
        padding: 20px 8px;
        border-bottom: 1px solid #e2ecf4;
    }

    &__orRuleBody {
        width: 100%;
    }

    &__andRuleContainer {
        display: flex;
        flex: 1;
        align-items: center;
        gap: 8px;
    }

    &__andRuleContent {
        flex: 1;
        display: grid;
        // 3rd item is value field which can depend on what property type is to have diff element with diff behavior
        // q-dropdown for example when opened, it will insert new element in causing the width to shift, hence minmax
        grid-template-columns: 40% 25% minmax(0, 35%);
        grid-column-gap: 15px;
        margin: 10px 0;
        font-size: 14px;
    }

    &__input {
        border: 1px solid #c9d1de;
        border-radius: 3px;
        padding: 5px;
    }

    input:focus-visible {
        outline: none;
        border-color: #2eb495;
    }

    &__riskRequirementTypeValueDropdown {
        width: 100%;
        // .q-field__marginal is the dropdown carret
        // .q-field__control is the dropdown container
        // .q-field__native is the dropdown div that contains the input
        .q-field__control, .q-field__native, .q-field__marginal {
            // Override the height of the quasar select to 40px
            height: 40px !important;
            min-height: 40px !important;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import get from 'lodash/get';
    import orderBy from 'lodash/orderBy';

    import { faXmark, faPlus } from '@fortawesome/pro-light-svg-icons';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import JsonSchema, { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isArray, isObject } from '@evidentid/json-schema/schemaChecks';
    import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
    import { CustomProperty, CustomPropertyType } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { GetRequirementTypeLabel } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import { AssignmentRuleValue, BasicAssignmentRule } from '@/modules/decisioning-criteria/types';
    import {
        allAssignmentOperators,
        allBaseAssignmentOperators,
        booleanAssignmentOperators,
        collateralCategoryOperators,
        credentialTypesOperators,
        enumAssignmentOperators,
        JsonRuleOperator,
        stringAssignmentOperators,
    } from '@/modules/decisioning-criteria/utils/assigmentRuleOperators';
    import { getLetterIndex } from '@/modules/decisioning-criteria/utils/getLetterIndex';
    import { tailwindColors } from '@/styles/variables/tailwind-colors/tailwindColors';
    import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
    import { isCredentialTypesCustomProperty } from '@/utils/isCredentialTypesCustomProperty';
    import { isEnumCustomProperty } from '@/utils/isEnumCustomProperty';

    const CREDENTIAL_TYPE_CUSTOM_PROPERTY_TYPE_VALUE = 'CREDENTIAL_TYPES';
    const ENUM_CUSTOM_PROPERTY_TYPE_VALUE = 'ENUM';
    // Total number of "Or if ..." groups one assignment rule may hold. UI-only guardrail: the backend
    // does not limit it. Evaluation cost is linear in groups, ~16us/entity at 50 groups (PRODUCT-26719).
    const MAX_ASSIGNMENT_RULE_GROUPS = 50;

    export default defineComponent({
        name: 'RiskProfileAssignmentRuleBased',
        components: {
            EidIcon,
            NewButton,
            LogoLoader,
            NewDropdown,
        },
        props: {
            rules: {
                type: Array as PropType<BasicAssignmentRule[][]>,
                default: (): BasicAssignmentRule[][] => [],
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                required: true,
            },
            loadingCustomProperties: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            collateralsOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            isDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            requirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            getRequirementTypeLabel: {
                type: Function as PropType<GetRequirementTypeLabel>,
                required: true,
            },
        },
        emits: [ 'close', 'update-rules' ],
        data() {
            return {
                faPlus,
                faXmark,
                alertRedColor: tailwindColors.eidDangerRed.DEFAULT,
                booleanOptions: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ] as DropdownOption[],
                clickOutExceptions: [
                    '.RiskProfileSaveButton__saveButton',
                    '.RiskProfileSaveButton__saveButton span',
                ],
            };
        },
        computed: {
            requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
                return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions;
            },
            credentialTypes(): TprmCredentialType[] {
                return useDashboardStore().credentialTypes.list || [];
            },
            onlyCredentialsRequirements(): TprmRequirementType[] {
                const requirements = this.credentialTypes.reduce((acc, ct) => {
                    if (ct.requirement) {
                        acc.add(ct.requirement);
                    }
                    return acc;
                }, new Set<TprmRequirementType>());
                return Array.from(requirements);
            },
            requirementTypeOptions(): DropdownOption[] {
                const options = this.onlyCredentialsRequirements.map((requirementType) => ({
                    label: this.getRequirementTypeLabel(requirementType),
                    value: requirementType,
                }));
                return orderBy(options, 'label');
            },
            collateralCustomProperty(): CustomProperty | null {
                return this.customProperties.find(isCollateralCustomProperty) || null;
            },
            categoryOption(): DropdownOption | null {
                return this.collateralCustomProperty
                    ? { label: 'Category', value: `${this.collateralCustomProperty.key}[items].category` }
                    : null;
            },
            customPropertiesOptions(): DropdownOption[] {
                if (this.collateralsOnly) {
                    const hasCollateralField = Boolean(this.collateralCustomProperty);
                    return hasCollateralField && this.categoryOption
                        ? [ this.categoryOption ]
                        : [];
                }
                return this.customProperties
                    .filter((insuredField) => 'type' in insuredField.schema && this.isAllowedCustomProperty(insuredField))
                    .map((insuredField) => ({ label: insuredField.name, value: insuredField.key }));
            },
            localRules(): BasicAssignmentRule[][] {
                if (!this.rules || this.rules.length === 0) {
                    return [ [
                        { operator: JsonRuleOperator.equal, insuredFieldKey: '', value: '' },
                    ] ] as BasicAssignmentRule[][];
                }

                return this.rules;
            },
            isAtMaxRuleGroups(): boolean {
                return this.localRules.length >= MAX_ASSIGNMENT_RULE_GROUPS;
            },
        },
        methods: {
            getLetterIndex,
            getFieldSchema(rule: BasicAssignmentRule): JsonSchema | null {
                // get top schema, traverse down until the desired schema the key indicates
                // first complete word without special character is insured field key ex. collaterals[items].category
                const insuredFieldKeyFromRule = rule.insuredFieldKey.match(/\w+/)?.[0] || '';
                const topSchema = this.customProperties
                    .find((insuredField) => insuredField.key === insuredFieldKeyFromRule)?.schema;
                const schemaWrapper = { [insuredFieldKeyFromRule]: topSchema };
                const schemaPath = rule.insuredFieldKey
                    // change dot charater into peroperties as it indicates object type
                    .replaceAll(/\./g, '.properties.')
                    // change [items] into .items to access array items schema
                    .replaceAll(/\[(\w+)\]/g, '.$1');
                return get(schemaWrapper, schemaPath) || null;
            },
            getCustomPropertyType(rule: BasicAssignmentRule): string {
                const customProperty = this.customProperties.find((property) => new RegExp(`^${property.key}`).test(rule.insuredFieldKey));
                if (customProperty && isCredentialTypesCustomProperty(customProperty)) {
                    return CREDENTIAL_TYPE_CUSTOM_PROPERTY_TYPE_VALUE;
                }
                if (customProperty && isEnumCustomProperty(customProperty)) {
                    return ENUM_CUSTOM_PROPERTY_TYPE_VALUE;
                }
                const schema = this.getFieldSchema(rule);
                return (schema && 'type' in schema ? schema.type : '');
            },
            getAvailableOperators(rule: BasicAssignmentRule): DropdownOption[] {
                const customProperty = this.customProperties.find((property) => new RegExp(`^${property.key}`).test(rule.insuredFieldKey));
                if (customProperty) {
                    if (isCollateralCustomProperty(customProperty)) {
                        return collateralCategoryOperators;
                    }
                    if (isCredentialTypesCustomProperty(customProperty)) {
                        return credentialTypesOperators;
                    }
                    if (isEnumCustomProperty(customProperty)) {
                        return enumAssignmentOperators;
                    }
                }
                const type = this.getCustomPropertyType(rule);
                switch (type) {
                    case 'boolean':
                        return booleanAssignmentOperators;
                    case 'string':
                        return stringAssignmentOperators;
                    default:
                        return allBaseAssignmentOperators;
                }
            },
            convertCustomPropertyToDropdownOption(value: string): DropdownOption | null {
                if (!value) {
                    return null;
                }
                if (this.collateralsOnly && this.categoryOption) {
                    return this.categoryOption;
                }
                return {
                    value,
                    label: this.customProperties.find((insuredField) => insuredField.key === value)?.name || '',
                };
            },
            convertToOperatorOption(value: string): DropdownOption | null {
                if (!value) {
                    return null;
                }
                return {
                    value,
                    label: allAssignmentOperators.find((option) => option.value === value)?.label || value,
                };
            },
            convertToRiskRequirementTypeOption(value: AssignmentRuleValue): DropdownOption {
                const riskRequirementType = this.requirementTypes.find((requirementType) => requirementType === value);
                return {
                    value,
                    label: riskRequirementType ? this.getRequirementTypeLabel(riskRequirementType) : '',
                };
            },
            convertToBooleanOption(value: AssignmentRuleValue): DropdownOption | null {
                if (value === true) {
                    return { value, label: 'Yes' };
                } else if (value === false) {
                    return { value, label: 'No' };
                } else {
                    return null;
                }
            },
            onCustomPropertyNameChange(groupIndex: number, basicRuleIndex: number, option: DropdownOption): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].insuredFieldKey = option.value;
                const customPropertyType = this.getCustomPropertyType(updatedRules[groupIndex][basicRuleIndex]);
                if (customPropertyType === 'boolean') {
                    updatedRules[groupIndex][basicRuleIndex].value = false;
                } else {
                    updatedRules[groupIndex][basicRuleIndex].value = '';
                }
                if (customPropertyType === CREDENTIAL_TYPE_CUSTOM_PROPERTY_TYPE_VALUE) {
                    updatedRules[groupIndex][basicRuleIndex].operator = JsonRuleOperator.containsAnyFrom;
                } else {
                    updatedRules[groupIndex][basicRuleIndex].operator = JsonRuleOperator.equal;
                }
                this.$emit('update-rules', updatedRules);
            },
            onOperatorChange(groupIndex: number, basicRuleIndex: number, option: DropdownOption): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].operator = option.value;
                this.$emit('update-rules', updatedRules);
            },
            onValueChange(groupIndex: number, basicRuleIndex: number, event: InputEvent): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].value = (event.target as HTMLInputElement).value;
                this.$emit('update-rules', updatedRules);
            },
            onBooleanValueChange(groupIndex: number, basicRuleIndex: number, option: DropdownOption): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].value = option.value;
                this.$emit('update-rules', updatedRules);
            },
            getEnumValueOptions(rule: BasicAssignmentRule): DropdownOption[] {
                const customProperty = this.customProperties.find((p) => p.key === rule.insuredFieldKey);
                const enumValues = (customProperty?.schema as JsonSchemaEnum<string>)?.enum ?? [];
                return enumValues.map((opt) => ({ label: opt, value: opt }));
            },
            convertToEnumValueOption(value: AssignmentRuleValue): DropdownOption | null {
                if (!value && value !== 0) {
                    return null;
                }
                return { label: String(value), value };
            },
            onEnumValueChange(groupIndex: number, basicRuleIndex: number, option: DropdownOption): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].value = option.value;
                this.$emit('update-rules', updatedRules);
            },
            onRiskRequirementTypeValueChange(groupIndex: number, basicRuleIndex: number, option: DropdownOption): void {
                if (!option) {
                    return;
                }
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex][basicRuleIndex].value = option.value;
                this.$emit('update-rules', updatedRules);
            },
            addBasicRule(groupIndex: number): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules[groupIndex].push({ operator: JsonRuleOperator.equal, insuredFieldKey: '', value: '' });
                this.$emit('update-rules', updatedRules);
            },
            addRulesGroup(): void {
                const updatedRules = this.localRules.map((basicRules) => basicRules.slice());
                updatedRules.push([ { operator: JsonRuleOperator.equal, insuredFieldKey: '', value: '' } ]);
                this.$emit('update-rules', updatedRules);
            },
            removeBasicRule(groupIndex: number, basicRuleIndex: number): void {
                if (this.isDisabled) {
                    return;
                }
                const updatedRules = this.localRules.map((basicRules, index) => (index === groupIndex
                    ? [ ...basicRules.slice(0, basicRuleIndex), ...basicRules.slice(basicRuleIndex + 1) ]
                    : basicRules.slice()));

                if (updatedRules[groupIndex].length === 0) {
                    updatedRules.splice(groupIndex, 1);
                }

                this.$emit('update-rules', updatedRules);
            },
            isCustomPropertySelected(rule: BasicAssignmentRule): boolean {
                return rule.insuredFieldKey.length > 0;
            },
            getInputValueType(basicRule: BasicAssignmentRule): string {
                return [ 'number', 'integer' ].includes(this.getCustomPropertyType(basicRule)) ? 'number' : 'text';
            },
            close(): void {
                this.$emit('close');
            },
            isAllowedCustomProperty(customProperty: CustomProperty): boolean {
                return isCredentialTypesCustomProperty(customProperty) ||
                    isEnumCustomProperty(customProperty) ||
                    (!isArray(customProperty.schema) && !isObject(customProperty.schema));
            },
        },
    });
</script>
